import { useQuery } from '@tanstack/react-query';

import {
  getBroadcastAISuggestion,
  getRecognitionAISuggestion,
  getTaskAISuggestion,
} from 'api/ai.service';
import { TABS } from 'components/common/AISuggestionWrapper';
import { useToast } from 'hooks/ui';
import { useEffect } from 'react';
import { useAISuggestionStore } from 'store/ai-suggestion';

type UseGenerateAISuggestionProps = {
  tab: string;
  field: string;
  text: string;
};

export const useGenerateAISuggestion = ({
  tab,
  field,
  text,
}: UseGenerateAISuggestionProps) => {
  const toast = useToast();
  const setActiveAISuggestion = useAISuggestionStore(
    (store) => store.setActiveAISuggestion,
  );

  const { isLoading, isFetching, data, refetch, error } = useQuery({
    queryKey: ['generateDescription', tab],
    queryFn: ({ signal }) => {
      if (tab === TABS.TASKS) {
        return getTaskAISuggestion(text, signal);
      }

      if (tab === TABS.RECOGNITIONS) {
        return getRecognitionAISuggestion(text, signal);
      }

      if (tab === TABS.BROADCASTS) {
        return getBroadcastAISuggestion(text, signal);
      }

      return null;
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (data) {
      setActiveAISuggestion({
        tab,
        field,
      });
    }
  }, [data, field, setActiveAISuggestion, tab]);

  useEffect(() => {
    if (error) {
      toast.error(
        error.message ??
          'Oops! Error executing prompt. Please retry. Reach out for help if persists. Thanks!',
      );
    }
  }, [error, toast]);

  return {
    isLoading: isLoading || isFetching,
    data,
    refetch,
  };
};
